import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import NavDropdownPanel, { type NavDropdownItem } from "./NavDropdownPanel";
import { useModuleSettings } from "@/hooks/useModuleSettings";
import { useAuthState } from "@/hooks/useAuthState";
import {
  cryptoItems,
  fiatItems,
  tradeItems,
  standaloneLinks,
  filterMenusForAuth,
} from "./navConfig";

type DropdownKey = "crypto" | "fiat" | "trade";

interface MenuDef {
  key: DropdownKey;
  label: string;
  items: NavDropdownItem[];
  width: string;
  columns?: 1 | 2;
  moduleKey: string;
}

const menuDefs: MenuDef[] = [
  { key: "crypto", label: "Crypto", items: cryptoItems, width: "min-w-[560px]", columns: 2, moduleKey: "landing_crypto" },
  { key: "fiat", label: "Fiat", items: fiatItems, width: "min-w-[560px]", columns: 2, moduleKey: "landing_fiat" },
  { key: "trade", label: "Trade", items: tradeItems, width: "min-w-[560px]", columns: 2, moduleKey: "landing_trade" },
];

export default function LandingDesktopNav() {
  const [openKey, setOpenKey] = useState<DropdownKey | null>(null);
  const enterTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const { isModuleEnabled, isLoading: modulesLoading } = useModuleSettings();
  const { isAuthed } = useAuthState();

  // Filter menus: hide entire dropdown if its module is disabled, filter sub-items by their moduleKey,
  // then apply guest allowlist when not authenticated.
  const menus = useMemo(() => {
    const moduleFiltered = menuDefs
      .filter((m) => isModuleEnabled(m.moduleKey))
      .map((m) => ({
        ...m,
        items: m.items.filter((item) => !item.moduleKey || isModuleEnabled(item.moduleKey)),
      }))
      .filter((m) => m.items.length > 0);
    return filterMenusForAuth(moduleFiltered, isAuthed);
  }, [isModuleEnabled, isAuthed]);

  // Public standalone links are limited to Notes and crypto markets.
  const visibleLinks = useMemo(() => {
    return standaloneLinks.filter((l) => isModuleEnabled(l.moduleKey));
  }, [isModuleEnabled]);

  const open = useCallback((key: DropdownKey) => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    if (enterTimer.current) clearTimeout(enterTimer.current);
    enterTimer.current = setTimeout(() => setOpenKey(key), 120);
  }, []);

  const close = useCallback(() => {
    if (enterTimer.current) clearTimeout(enterTimer.current);
    leaveTimer.current = setTimeout(() => setOpenKey(null), 150);
  }, []);

  const cancelClose = useCallback(() => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenKey(null);
    };
    const onClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenKey(null);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  if (modulesLoading) {
    // Sized placeholder so layout doesn't shift and items don't pop in.
    return (
      <div className="flex items-center gap-0.5 xl:gap-1" aria-hidden>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-9 w-20 rounded-lg bg-muted/40 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div ref={navRef} className="flex items-center gap-0.5 xl:gap-1">
      {menus.map((menu) => (
        <div
          key={menu.key}
          className="relative"
          onMouseEnter={() => open(menu.key)}
          onMouseLeave={close}
        >
          <button
            onClick={() => setOpenKey(openKey === menu.key ? null : menu.key)}
            onMouseEnter={cancelClose}
            className={`flex items-center gap-1 px-2 xl:px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              openKey === menu.key
                ? "text-primary bg-primary/5"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {menu.label}
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform duration-200 ${
                openKey === menu.key ? "rotate-180" : ""
              }`}
            />
          </button>
          <NavDropdownPanel
            items={menu.items}
            isOpen={openKey === menu.key}
            onItemClick={() => setOpenKey(null)}
            className={menu.width}
            columns={menu.columns}
          />
        </div>
      ))}

      {visibleLinks.map((link) => (
        <Link
          key={link.href}
          to={link.href}
          className="px-2 xl:px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg"
        >
          {link.label}
        </Link>
      ))}
    </div>
  );
}
